"use client";

import type { IiqCheckStoryblok } from "@/component-types-sb";
import { siteUrl } from "@/config/site";
import { usePathname } from "next/navigation";
import Script from "next/script";
import { useEffect } from "react";

type IiqCheckProps = {
  blok: IiqCheckStoryblok;
};

const IiqCheck = ({ blok }: IiqCheckProps) => {
  const pathname = usePathname();

  const id = blok.id;
  const r = siteUrl + pathname;

  const fixIframeAccessibility = () => {
    // Wait for the iframe to be rendered by the external script
    setTimeout(() => {
      const container = document.getElementById(`iiqcheck_widget_${id}`);
      if (container) {
        const iframe = container.querySelector('iframe');
        if (iframe && !iframe.getAttribute('title')) {
          iframe.setAttribute('title', 'IIQCheck Bewertungswidget - Kundenbewertungen und Verfügbarkeitsprüfung');
          iframe.setAttribute('aria-label', 'IIQCheck Widget für Hotelbewertungen und Buchungsanfragen');

          // Add role for better semantic meaning
          iframe.setAttribute('role', 'application');

          // Add loading state announcement for screen readers
          iframe.setAttribute('aria-live', 'polite');
          iframe.setAttribute('aria-busy', 'false');
        }
      }
    }, 1000); // Wait 1 second for script to load and render
  };

  // Also check periodically in case the iframe loads later
  useEffect(() => {
    const checkInterval = setInterval(() => {
      const container = document.getElementById(`iiqcheck_widget_${id}`);
      if (container) {
        const iframe = container.querySelector('iframe');
        if (iframe && !iframe.getAttribute('title')) {
          iframe.setAttribute('title', 'IIQCheck Bewertungswidget - Kundenbewertungen und Verfügbarkeitsprüfung');
          iframe.setAttribute('aria-label', 'IIQCheck Widget für Hotelbewertungen und Buchungsanfragen');
          iframe.setAttribute('role', 'application');
          iframe.setAttribute('aria-live', 'polite');
          iframe.setAttribute('aria-busy', 'false');

          // Clear interval once iframe is fixed
          clearInterval(checkInterval);
        }
      }
    }, 500);

    // Clear interval after 10 seconds to avoid infinite checking
    const timeout = setTimeout(() => {
      clearInterval(checkInterval);
    }, 10000);

    return () => {
      clearInterval(checkInterval);
      clearTimeout(timeout);
    };
  }, [id]);

  return (
    <>
      {/* Loading indicator for screen readers */}
      <div
        id={`iiqcheck_description_${id}`}
        className="sr-only"
        aria-live="polite"
      >
        IIQCheck Widget wird geladen...
      </div>

      <div
        id={`iiqcheck_widget_${id}`}
        className="max-w-[200px] mx-auto"
        role="region"
        aria-label="Hotelbewertungen und Verfügbarkeitsprüfung"
        aria-describedby={`iiqcheck_description_${id}`}
      >
      </div>
      <Script
        src={`https://app.iiq-check.de/hotels/${id}/widget_configurations/w.js?r=${r}`}
        strategy="afterInteractive"
        onLoad={fixIframeAccessibility}
        onError={() => {
          const description = document.getElementById(`iiqcheck_description_${id}`);
          if (description) {
            description.textContent = "IIQCheck Widget konnte nicht geladen werden.";
          }
        }}
      />
    </>
  );
};

export default IiqCheck;
